import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaClock, FaEye, FaArrowLeft, FaTag, FaTwitter, FaLinkedin, FaLink } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { safeFetch } from '../utils/apiUtils';
import useSEO, { SITE_URL } from '../utils/useSEO';
import './BlogDetail.css';

const API_BASE = 'https://aayushrealproperties.in/nimaibackend/public/api';

const CATEGORY_COLORS = {
    'Web Development':    '#FF9F1C',
    'Mobile Development': '#3b82f6',
    'UI/UX Design':       '#a855f7',
    'SEO & Performance':  '#10b981',
    'Business Growth':    '#ef4444',
    'Case Study':         '#0ea5e9',
    'General':            '#64748b',
};

const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

const buildArticleSchema = (post) => ({
    "@context": "https://schema.org",
    "@graph": [
        {
            "@type": "Article",
            "@id": `${SITE_URL}/blog/${post.slug}#article`,
            "headline": post.title,
            "description": post.excerpt,
            "image": post.featured_image || `${SITE_URL}/og-image.jpg`,
            "author": { "@type": "Organization", "@id": `${SITE_URL}/#business`, "name": post.author || "Nimai Solutions" },
            "publisher": { "@type": "Organization", "@id": `${SITE_URL}/#business`, "name": "Nimai Solutions", "url": SITE_URL },
            "datePublished": post.created_at,
            "dateModified":  post.updated_at || post.created_at,
            "url": `${SITE_URL}/blog/${post.slug}`,
            "articleSection": post.category,
        },
        {
            "@type": "BreadcrumbList",
            "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home",      "item": SITE_URL },
                { "@type": "ListItem", "position": 2, "name": "Blog",      "item": `${SITE_URL}/blog` },
                { "@type": "ListItem", "position": 3, "name": post.title,  "item": `${SITE_URL}/blog/${post.slug}` }
            ]
        }
    ]
});

/* ─── SHARE BUTTONS ─── */
const ShareButtons = ({ post }) => {
    const url = `${SITE_URL}/blog/${post.slug}`;
    const [copied, setCopied] = useState(false);

    const copyLink = () => {
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="share-row">
            <span className="share-label">Share this article</span>
            <div className="share-btns">
                <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(post.title)}`}
                   target="_blank" rel="noopener noreferrer" className="share-btn twitter" aria-label="Share on Twitter">
                    <FaTwitter />
                </a>
                <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(post.title)}`}
                   target="_blank" rel="noopener noreferrer" className="share-btn linkedin" aria-label="Share on LinkedIn">
                    <FaLinkedin />
                </a>
                <button className="share-btn copy" onClick={copyLink} aria-label="Copy link">
                    <FaLink />
                    {copied && <span className="copy-tooltip">Copied!</span>}
                </button>
            </div>
        </div>
    );
};

/* ─── RELATED CARD ─── */
const RelatedCard = ({ post }) => {
    const navigate = useNavigate();
    const color = CATEGORY_COLORS[post.category] || CATEGORY_COLORS.General;
    return (
        <motion.article
            className="related-card"
            onClick={() => { navigate(`/blog/${post.slug}`); window.scrollTo({ top: 0 }); }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            whileHover={{ y: -6 }}
        >
            <div className="related-card-img">
                {post.featured_image
                    ? <img src={post.featured_image} alt={post.title} loading="lazy" />
                    : <div className="related-placeholder" style={{ background: `${color}18` }}>✍</div>
                }
                <span className="related-cat-tag" style={{ background: color }}>{post.category}</span>
            </div>
            <div className="related-card-body">
                <h3>{post.title}</h3>
                <span className="related-meta"><FaClock /> {post.read_time} min read</span>
            </div>
        </motion.article>
    );
};

/* ─── MAIN COMPONENT ─── */
const BlogDetail = () => {
    const { slug }  = useParams();
    const navigate  = useNavigate();
    const [post, setPost]           = useState(null);
    const [related, setRelated]     = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError]         = useState(false);

    useSEO(post ? {
        title:       `${post.title} | Nimai Solutions Blog`,
        description: post.excerpt || `Read about ${post.title} on the Nimai Solutions blog.`,
        canonical:   `${SITE_URL}/blog/${post.slug}`,
        keywords:    `${post.category}, Nimai Solutions, Vadodara, ${post.title}`,
        ogImage:     post.featured_image,
        schema:      buildArticleSchema(post),
    } : {
        title:       'Loading... | Nimai Solutions Blog',
        description: 'Nimai Solutions Blog',
        canonical:   `${SITE_URL}/blog/${slug}`,
    });

    useEffect(() => {
        const fetchPost = async () => {
            setIsLoading(true);
            setError(false);
            try {
                const { ok, data: result } = await safeFetch(`${API_BASE}/blog-posts/${slug}`);
                const p = result?.data ?? result;
                if (ok && p?.id) {
                    setPost(p);
                    const { ok: ok2, data: list } = await safeFetch(`${API_BASE}/blog-posts`);
                    if (ok2) {
                        const arr = Array.isArray(list) ? list : list?.data || [];
                        setRelated(arr.filter(r => r.slug !== slug && r.category === p.category).slice(0, 3));
                    }
                } else {
                    setError(true);
                }
            } catch {
                setError(true);
            } finally {
                setIsLoading(false);
            }
        };
        fetchPost();
        window.scrollTo({ top: 0 });
    }, [slug]);

    /* LOADING */
    if (isLoading) return (
        <div className="blog-detail-container">
            <Navbar />
            <div className="detail-skeleton-hero" />
            <div className="detail-skeleton-body">
                {[...Array(8)].map((_, i) => (
                    <div key={i} className="detail-skeleton-line" style={{ width: i % 3 === 2 ? '55%' : '100%' }} />
                ))}
            </div>
            <Footer />
        </div>
    );

    /* 404 */
    if (error || !post) return (
        <div className="blog-detail-container">
            <Navbar />
            <div className="detail-404">
                <span>🔍</span>
                <h2>Post Not Found</h2>
                <p>This article doesn't exist or has been removed.</p>
                <button className="btn-back-blog" onClick={() => navigate('/blog')}>
                    <FaArrowLeft /> Back to Blog
                </button>
            </div>
            <Footer />
        </div>
    );

    const color = CATEGORY_COLORS[post.category] || CATEGORY_COLORS.General;

    return (
        <div className="blog-detail-container">
            <Navbar />

            {/* ── HERO ── */}
            <section className="detail-hero">
                <div className="detail-hero-grid" />
                {post.featured_image && (
                    <div className="detail-hero-img-bg">
                        <img src={post.featured_image} alt={post.title} />
                        <div className="detail-hero-overlay" />
                    </div>
                )}
                <motion.div
                    className="detail-hero-content"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                >
                    <button className="btn-back-blog" onClick={() => navigate('/blog')}>
                        <FaArrowLeft /> All Articles
                    </button>

                    <div className="detail-meta-row">
                        <span className="detail-cat-pill" style={{ background: color }}>
                            <FaTag /> {post.category}
                        </span>
                        <span><FaClock /> {post.read_time} min read</span>
                        <span><FaEye /> {post.views} views</span>
                        <span>{formatDate(post.created_at)}</span>
                    </div>

                    <h1>{post.title}</h1>
                    {post.excerpt && <p className="detail-excerpt">{post.excerpt}</p>}

                    <div className="detail-author">
                        <div className="author-badge">NS</div>
                        <span>{post.author}</span>
                    </div>
                </motion.div>
            </section>

            {/* ── ARTICLE BODY ── */}
            <div className="detail-body-wrap">
                <motion.article
                    className="detail-article"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    dangerouslySetInnerHTML={{ __html: post.content }}
                />
                <ShareButtons post={post} />
            </div>

            {/* ── RELATED ── */}
            {related.length > 0 && (
                <section className="detail-related">
                    <div className="detail-related-inner">
                        <h2>Related <span>Articles</span></h2>
                        <div className="related-grid">
                            {related.map(r => <RelatedCard key={r.id} post={r} />)}
                        </div>
                    </div>
                </section>
            )}

            {/* ── CTA ── */}
            <section className="detail-cta-section">
                <div className="detail-cta-card">
                    <div className="cta-blob blob-1" />
                    <div className="cta-blob blob-2" />
                    <div className="detail-cta-inner">
                        <span className="cta-tag">Let's Build Something</span>
                        <h2>Ready to bring your <span>vision</span> to life?</h2>
                        <p>Talk to the Nimai Solutions team about your next web or mobile project.</p>
                        <button className="cta-primary" onClick={() => navigate('/contact')}>
                            Get a Free Consultation
                        </button>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default BlogDetail;
